import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, getAccessToken, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useLookup } from "../context/LookupContext.js";
import { DatePicker } from "../components/DatePicker.js";
import { AsyncButton } from "../components/ui/async-button.js";
import { SelectField } from "../components/ui/select-field.js";

import { HR_ROLES, FINANCE_ROLES } from "@hrms/types";
import { usePageBack } from "../context/PageBackContext.js";

function decodeToken(token: string): { role: string; sub: string } {
  try {
    const p = JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>;
    return { role: String(p["role"] ?? ""), sub: String(p["sub"] ?? "") };
  } catch { return { role: "", sub: "" }; }
}

interface Contractor {
  id: string; poNumber: string; rateType: string; rateAmount: string; rateCurrency: string;
  taxId: string | null; taxIdType: string | null; bgvStatus: string;
  financeApproved: boolean; financeApprovedAt: string | null;
  contractStart: string; contractEnd: string | null;
  createdAt: string; notes: string | null;
  employee: { id: string; firstName: string; lastName: string; employeeCode: string; status: string; currency: string };
}

const bgvColor = (s: string): React.CSSProperties => {
  if (s === "VERIFIED") return { background: "var(--color-success-bg)", color: "var(--color-success-fg)" };
  if (s === "FAILED") return { background: "var(--color-error-bg)", color: "var(--color-error-fg)" };
  return { background: "var(--color-warning-bg)", color: "var(--color-warning-fg)" };
};

const inputStyle: React.CSSProperties = {
  padding: "8px 10px", border: "1px solid var(--color-surface-200)",
  borderRadius: "var(--radius-md)", fontSize: "14px", background: "var(--color-surface-0)", width: "100%",
};

const BLANK = { employeeId: "", poNumber: "", rateType: "DAILY", rateAmount: "", rateCurrency: "INR", taxId: "", taxIdType: "", contractStart: "", contractEnd: "" };

export default function ContractorsPage() {
  usePageBack("/dashboard");
  const navigate = useNavigate();
  const rateTypes = useLookup("CONTRACTOR_RATE_TYPE");
  const currencies = useLookup("CURRENCY");
  const taxIdTypes = useLookup("CONTRACTOR_TAX_ID_TYPE");
  const accessToken = getAccessToken() ?? "";
  const { role, sub } = decodeToken(accessToken);
  const isHR = HR_ROLES.has(role);
  const isFinance = FINANCE_ROLES.has(role);
  const canView = isHR || isFinance;

  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Contractor | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...BLANK });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [filterBgv, setFilterBgv] = useState("");

  async function load() {
    setLoading(true);
    try {
      const params = filterBgv ? `?bgvStatus=${filterBgv}` : "";
      const res = await api.get<{ data: Contractor[] }>(`/contractors${params}`);
      setContractors(res.data.data);
    } catch (err) { logError("ContractorsPage.load", err); } finally { setLoading(false); }
  }

  useEffect(() => { if (canView) load(); else setLoading(false); }, [filterBgv]);

  async function create() {
    setSaving(true); setError("");
    try {
      await api.post("/contractors", {
        ...form,
        rateAmount: parseFloat(form.rateAmount),
        taxId: form.taxId || undefined,
        taxIdType: form.taxIdType || undefined,
        contractEnd: form.contractEnd || undefined,
      });
      setShowForm(false); setForm({ ...BLANK }); load();
    } catch (e: unknown) {
      logError("ContractorsPage.create", e);
      setError(errMsg(e) ?? "Failed");
      throw e;
    } finally { setSaving(false); }
  }

  async function financeApprove(employeeId: string) {
    try {
      await api.patch(`/contractors/${employeeId}/finance-approve`);
      load(); if (selected?.employee.id === employeeId) setSelected(null);
    } catch (e: unknown) {
      logError("ContractorsPage.financeApprove", e);
      alert(errMsg(e) ?? "Failed");
    }
  }

  async function updateBgv(employeeId: string, status: string) {
    try {
      await api.patch(`/contractors/${employeeId}`, { bgvStatus: status });
      load(); if (selected?.employee.id === employeeId) setSelected(null);
    } catch (e: unknown) {
      logError("ContractorsPage.updateBgv", e);
      alert(errMsg(e) ?? "Failed");
    }
  }

  if (!canView) return (
    <div style={{ padding: "var(--space-8)", color: "var(--color-error-fg)" }}>
      You don't have access to this section.
    </div>
  );

  return (
    <div style={{ padding: "var(--space-8)", maxWidth: 1100 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--color-text-900)" }}>Contractors & 1099</h1>
        {isHR && (
          <button onClick={() => { setShowForm(true); setError(""); }}
            style={{ padding: "8px 16px", background: "var(--color-brand-600)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
            + Add Contractor Record
          </button>
        )}
      </div>

      {showForm && (
        <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: 20, marginBottom: 20 }}>
          <h3 style={{ fontWeight: 600, fontSize: 15, marginBottom: 16 }}>New Contractor Record</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Employee ID (CUID) *</label>
              <input style={inputStyle} value={form.employeeId} onChange={e => setForm(f => ({ ...f, employeeId: e.target.value }))} placeholder="cjld2cy..." />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>PO Number *</label>
              <input style={inputStyle} value={form.poNumber} onChange={e => setForm(f => ({ ...f, poNumber: e.target.value }))} placeholder="PO-2026-001" />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Rate Type</label>
              <SelectField value={form.rateType} onChange={e => setForm(f => ({ ...f, rateType: e.target.value }))}>
                {rateTypes.map(o => <option key={o.value} value={o.value}>{o.displayName}</option>)}
              </SelectField>
            </div>
            <div>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Rate Amount *</label>
              <input type="number" step="0.01" style={inputStyle} value={form.rateAmount} onChange={e => setForm(f => ({ ...f, rateAmount: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Currency</label>
              <SelectField value={form.rateCurrency} onChange={e => setForm(f => ({ ...f, rateCurrency: e.target.value }))}>
                {currencies.map(o => <option key={o.value} value={o.value}>{o.displayName}</option>)}
              </SelectField>
            </div>
            <div>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Tax ID Type</label>
              <SelectField value={form.taxIdType} onChange={e => setForm(f => ({ ...f, taxIdType: e.target.value }))}>
                <option value="">None</option>
                {taxIdTypes.map(o => <option key={o.value} value={o.value}>{o.displayName}</option>)}
              </SelectField>
            </div>
            <div>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Tax ID</label>
              <input style={inputStyle} value={form.taxId} onChange={e => setForm(f => ({ ...f, taxId: e.target.value }))} placeholder="GSTIN / EIN" />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Contract Start *</label>
              <DatePicker style={inputStyle} value={form.contractStart} onChange={v => setForm(f => ({ ...f, contractStart: v }))} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Contract End</label>
              <DatePicker style={inputStyle} value={form.contractEnd} onChange={v => setForm(f => ({ ...f, contractEnd: v }))} />
            </div>
          </div>
          {error && <p style={{ color: "var(--color-error)", fontSize: 13, marginBottom: 8 }}>{error}</p>}
          <div style={{ display: "flex", gap: 8 }}>
            <AsyncButton onClick={() => create()} disabled={saving || !form.employeeId || !form.poNumber || !form.rateAmount || !form.contractStart}>
              Create
            </AsyncButton>
            <button onClick={() => setShowForm(false)}
              style={{ padding: "7px 16px", background: "transparent", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 12, display: "flex", gap: 8 }}>
        {["", "PENDING", "VERIFIED", "FAILED"].map(s => (
          <button key={s} onClick={() => setFilterBgv(s)} style={{
            padding: "5px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer",
            background: filterBgv === s ? "var(--color-brand-600)" : "var(--color-surface-100)",
            color: filterBgv === s ? "#fff" : "var(--color-text-600)",
            border: filterBgv === s ? "none" : "1px solid var(--color-surface-200)",
          }}>{s || "All BGV"}</button>
        ))}
      </div>

      {loading ? <p style={{ color: "var(--color-text-400)", fontSize: 14 }}>Loading…</p> : contractors.length === 0 ? (
        <p style={{ color: "var(--color-text-400)", fontSize: 14 }}>No contractor records found.</p>
      ) : (
        <div style={{ overflowX: "auto", background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--color-surface-50)", borderBottom: "1px solid var(--color-surface-200)" }}>
                {["Employee","PO Number","Rate","BGV","Finance","Contract Period","Actions"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "10px 14px", color: "var(--color-text-500)", fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {contractors.map(c => (
                <tr key={c.id} style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
                  <td style={{ padding: "9px 14px" }}>
                    <div style={{ fontWeight: 600, cursor: "pointer" }} onClick={() => navigate(`/employees/${c.employee.id}/edit`)} onMouseEnter={e => (e.currentTarget.style.textDecoration = "underline")} onMouseLeave={e => (e.currentTarget.style.textDecoration = "none")}>{c.employee.firstName} {c.employee.lastName}</div>
                    <div style={{ fontSize: 11, color: "var(--color-text-400)" }}>{c.employee.employeeCode}</div>
                  </td>
                  <td style={{ padding: "9px 14px", fontFamily: "monospace", fontSize: 12 }}>{c.poNumber}</td>
                  <td style={{ padding: "9px 14px", whiteSpace: "nowrap" }}>
                    {Number(c.rateAmount).toLocaleString()} {c.rateCurrency}/{c.rateType.toLowerCase()}
                  </td>
                  <td style={{ padding: "9px 14px" }}>
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, fontWeight: 600, ...bgvColor(c.bgvStatus) }}>{c.bgvStatus}</span>
                  </td>
                  <td style={{ padding: "9px 14px" }}>
                    {c.financeApproved
                      ? <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, fontWeight: 600, background: "var(--color-success-bg)", color: "var(--color-success-fg)" }}>Approved</span>
                      : <span style={{ fontSize: 11, color: "var(--color-text-400)" }}>Pending</span>}
                  </td>
                  <td style={{ padding: "9px 14px", fontSize: 12, whiteSpace: "nowrap" }}>
                    {c.contractStart.slice(0, 10)} → {c.contractEnd ? c.contractEnd.slice(0, 10) : "Open"}
                  </td>
                  <td style={{ padding: "9px 14px" }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      {isHR && c.bgvStatus === "PENDING" && (
                        <>
                          <button onClick={() => updateBgv(c.employee.id, "VERIFIED")}
                            style={{ padding: "3px 8px", fontSize: 11, background: "var(--color-success-bg)", color: "var(--color-success-fg)", border: "none", borderRadius: "var(--radius-sm)", cursor: "pointer", fontWeight: 600 }}>
                            Verify BGV
                          </button>
                          <button onClick={() => updateBgv(c.employee.id, "FAILED")}
                            style={{ padding: "3px 8px", fontSize: 11, background: "var(--color-error-bg)", color: "var(--color-error-fg)", border: "none", borderRadius: "var(--radius-sm)", cursor: "pointer", fontWeight: 600 }}>
                            Fail
                          </button>
                        </>
                      )}
                      {isFinance && !c.financeApproved && (
                        <button onClick={() => financeApprove(c.employee.id)}
                          style={{ padding: "3px 8px", fontSize: 11, background: "var(--color-info-bg)", color: "var(--color-info-fg)", border: "none", borderRadius: "var(--radius-sm)", cursor: "pointer", fontWeight: 600 }}>
                          Finance Approve
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {sub && <span style={{ display: "none" }}>{sub}</span>}
    </div>
  );
}
